import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, Package, FileText, Settings, LogOut, 
  Menu, X, Upload, Trash2
} from 'lucide-react';
import peptLogo from '../../assets/pept-logo-cropped.png';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCOAs() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [productName, setProductName] = useState('');
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCOAs();
  }, [token]);

  const fetchCOAs = async () => {
    try {
      const res = await fetch(`${API}/api/admin/coas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCoas(data);
      }
    } catch (err) {
      console.error('Error fetching COAs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !productName) {
      setMessage('Please enter a product name first');
      return;
    }

    setUploading(true);
    setMessage('');
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('product_name', productName);

    try {
      const res = await fetch(`${API}/api/admin/upload/coa`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setMessage(`COA uploaded successfully! Path: ${data.path}`);
        setProductName('');
      }
    } catch (err) {
      setMessage('Error uploading COA');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const menuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { label: 'Peptides', icon: Package, path: '/admin/peptides' },
    { label: 'COA Library', icon: FileText, path: '/admin/coas', active: true },
    { label: 'Site Settings', icon: Settings, path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-neutral-900 flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-neutral-800 border-r border-neutral-700 transform transition-transform lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 border-b border-neutral-700">
          <img src={peptLogo} alt="PEP-T.COM" className="h-10" />
          <p className="text-neutral-400 text-xs mt-1">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                item.active 
                  ? 'bg-red-600 text-white' 
                  : 'text-neutral-300 hover:bg-neutral-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-neutral-400 hover:text-red-400 w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Top bar */}
        <header className="bg-neutral-800 border-b border-neutral-700 px-4 py-4 flex items-center justify-between lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-neutral-400 hover:text-white lg:hidden"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              COA Library Management
            </h1>
          </div>
          <Link to="/coa" target="_blank" className="text-sm text-neutral-400 hover:text-white">
            View Library →
          </Link>
        </header>

        {/* Content */}
        <div className="p-4 lg:p-6 space-y-6">
          {/* Upload Section */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-400" />
              Upload New COA
            </h2>

            {message && (
              <div className={`p-3 rounded-sm mb-4 ${message.includes('Error') ? 'bg-red-900/30 text-red-400' : 'bg-green-900/30 text-green-400'}`}>
                {message}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                placeholder="Product name (e.g., Tirzepatide 60mg)"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="flex-1 px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-sm text-white placeholder-neutral-500"
              />
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
              />
              
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !productName}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-sm hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Upload COA'}
              </button>
            </div>
          </div>

          {/* Current COAs */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Current COA Library ({coas.length} certificates)
            </h2>

            {loading ? (
              <p className="text-neutral-400">Loading COAs...</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {coas.map((coa) => (
                  <div key={coa.id} className="bg-neutral-900 rounded-sm overflow-hidden group relative">
                    <img 
                      src={coa.image} 
                      alt={coa.product}
                      className="w-full h-24 object-cover"
                    />
                    <div className="p-2">
                      <p className="text-xs text-white truncate">{coa.product}</p>
                      <p className="text-xs text-green-400">{coa.purity}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-neutral-500 text-sm">
            COAs are now managed through the database. Upload new COAs using the form above.
          </p>
        </div>
      </main>
    </div>
  );
}
